"use client";

import React, { useState, useCallback, useEffect } from "react";
import { Button } from "@/app/_module/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/app/_module/components/ui/card";
import { Input } from "@/app/_module/components/ui/input";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Badge } from "@/app/_module/components/ui/badge";
import {
  Megaphone, Plus, ArrowLeft, Trash2, Edit3, Eye,
  AlertTriangle, CheckCircle2, Clock, Users,
} from "lucide-react";
import AgencyTokenManager from "@/app/_module/services/agencyTokenManager";
import {
  useGetAnnouncements,
  useCreateAnnouncement,
  useUpdateAnnouncement,
  useDeleteAnnouncement,
  useGetAnnouncementDetail,
} from "../../hooks/use-announcements";
import type { Announcement, AnnouncementAudienceMember, CreateAnnouncementRequest } from "../../services/announcement-services";
import { searchAnnouncementAudience } from "../../services/announcement-services";
import { useAnnouncementAudience } from "../../hooks/use-announcements";

function formatDate(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateString;
  }
}

function formatDateTime(dateString: string): string {
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
}

function truncateText(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trimEnd() + "...";
}

const emptyForm: CreateAnnouncementRequest = {
  title: "",
  body: "",
  priority: "normal",
  isBlocking: true,
  notifyNewAgents: false,
};

export default function AgencyAnnouncements() {
  const [agencyId, setAgencyId] = useState(() => AgencyTokenManager.getCurrentAgencyId() || "");

  useEffect(() => {
    const handleAgencyChange = () => {
      setAgencyId(AgencyTokenManager.getCurrentAgencyId() || "");
      setPage(1);
      setView("list");
    };

    window.addEventListener("insuracentral-agency-change", handleAgencyChange);
    return () => window.removeEventListener("insuracentral-agency-change", handleAgencyChange);
  }, []);

  // View state
  const [view, setView] = useState<"list" | "create" | "edit" | "detail">("list");
  const [selectedAnnouncementId, setSelectedAnnouncementId] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Form state
  const [formData, setFormData] = useState<CreateAnnouncementRequest>({ ...emptyForm });
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Audience: everyone in the tree (default) or only the people picked below.
  const [audienceMode, setAudienceMode] = useState<"everyone" | "people" | "owners">("everyone");
  const targeted = audienceMode !== "everyone";
  const [recipients, setRecipients] = useState<AnnouncementAudienceMember[]>([]);
  const [audienceQuery, setAudienceQuery] = useState("");
  const { data: audienceResults = [], isFetching: isSearchingAudience } = useAnnouncementAudience(targeted ? agencyId : "", audienceQuery);

  // Queries & mutations
  const { data: listData, isLoading: isLoadingList } = useGetAnnouncements(agencyId, page);
  const { data: detailData, isLoading: isLoadingDetail } = useGetAnnouncementDetail(
    agencyId,
    selectedAnnouncementId || ""
  );
  const createMutation = useCreateAnnouncement(agencyId);
  const updateMutation = useUpdateAnnouncement(agencyId);
  const deleteMutation = useDeleteAnnouncement(agencyId);

  // Navigation helpers
  const resetAudience = useCallback(() => {
    setAudienceMode("everyone");
    setRecipients([]);
    setAudienceQuery("");
  }, []);

  const goToList = useCallback(() => {
    setView("list");
    setSelectedAnnouncementId(null);
    setFormData({ ...emptyForm });
    resetAudience();
  }, [resetAudience]);

  const goToCreate = useCallback(() => {
    setFormData({ ...emptyForm });
    resetAudience();
    setView("create");
  }, [resetAudience]);

  const goToEdit = useCallback((announcement: Announcement) => {
    setFormData({
      title: announcement.title,
      body: announcement.body,
      priority: announcement.priority,
      isBlocking: announcement.isBlocking,
      notifyNewAgents: announcement.notifyNewAgents ?? false,
    });
    setSelectedAnnouncementId(announcement.id);
    // Re-hydrate the chips by id so the names and agencies show.
    const ids = announcement.recipientUserIds || [];
    setAudienceMode(ids.length > 0 ? "people" : "everyone");
    setRecipients([]);
    setAudienceQuery("");
    if (ids.length > 0) {
      searchAnnouncementAudience(agencyId, "", ids).then(setRecipients).catch(() => setRecipients([]));
    }
    setView("edit");
  }, [agencyId]);

  // Preset: every owner of every agency below (and including) this one, added as chips they can still trim.
  const [isAddingOwners, setIsAddingOwners] = useState(false);
  const addAllOwners = useCallback(async () => {
    setIsAddingOwners(true);
    try {
      const owners = await searchAnnouncementAudience(agencyId, "", [], { role: "owners", limit: 500 });
      setRecipients((prev) => {
        const seen = new Set(prev.map((r) => r.userId));
        return [...prev, ...owners.filter((o) => !seen.has(o.userId))];
      });
    } catch {
      // toast comes from the api layer
    } finally {
      setIsAddingOwners(false);
    }
  }, [agencyId]);

  // Picking "All owners below you" loads them straight into the chips; the other two start empty.
  const chooseAudience = useCallback(async (mode: "everyone" | "people" | "owners") => {
    setAudienceMode(mode);
    setAudienceQuery("");
    if (mode === "everyone") { setRecipients([]); return; }
    if (mode === "owners") await addAllOwners();
  }, [addAllOwners]);

  const addRecipient = useCallback((member: AnnouncementAudienceMember) => {
    setRecipients((prev) => (prev.some((r) => r.userId === member.userId) ? prev : [...prev, member]));
  }, []);

  const removeRecipient = useCallback((userId: string) => {
    setRecipients((prev) => prev.filter((r) => r.userId !== userId));
  }, []);


  const goToDetail = useCallback((announcementId: string) => {
    setSelectedAnnouncementId(announcementId);
    setView("detail");
  }, []);

  // Form handlers
  const handleFormChange = useCallback(
    (field: keyof CreateAnnouncementRequest, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleSubmit = useCallback(async () => {
    if (!formData.title.trim() || !formData.body.trim()) return;
    if (targeted && recipients.length === 0) return;

    const payload: CreateAnnouncementRequest = {
      ...formData,
      recipientUserIds: targeted ? recipients.map((r) => r.userId) : null,
    };
    try {
      if (view === "edit" && selectedAnnouncementId) {
        await updateMutation.mutateAsync({
          announcementId: selectedAnnouncementId,
          data: payload,
        });
      } else {
        await createMutation.mutateAsync(payload);
      }
      goToList();
    } catch {
      // Error already handled by mutation onError callback
    }
  }, [view, formData, targeted, recipients, selectedAnnouncementId, createMutation, updateMutation, goToList]);

  const handleDelete = useCallback(
    async (announcementId: string) => {
      try {
        await deleteMutation.mutateAsync(announcementId);
        setConfirmDeleteId(null);
        if (view === "detail") {
          goToList();
        }
      } catch {
        // Error already handled by mutation onError callback
      }
    },
    [deleteMutation, view, goToList]
  );

  // ---------- RENDER: List View ----------
  if (view === "list") {
    const announcements = listData?.announcements ?? [];
    const totalPages = listData?.totalPages ?? 1;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            <h2 className="text-xl font-semibold">Announcements</h2>
          </div>
          <Button onClick={goToCreate} size="sm">
            <Plus className="h-4 w-4 mr-1" />
            New Announcement
          </Button>
        </div>

        {/* Loading */}
        {isLoadingList && (
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading announcements...</p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isLoadingList && announcements.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Megaphone className="h-12 w-12 text-muted-foreground/40 mb-4" />
              <p className="text-muted-foreground text-center max-w-md">
                No announcements yet. Create your first announcement to communicate with your agency.
              </p>
              <Button onClick={goToCreate} className="mt-4" variant="outline">
                <Plus className="h-4 w-4 mr-1" />
                Create Announcement
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Announcement cards */}
        {!isLoadingList && announcements.length > 0 && (
          <div className="space-y-3">
            {announcements.map((announcement) => {
              const ackCount = announcement.acknowledgmentCount ?? announcement.acknowledgments?.length ?? 0;

              return (
                <div
                  key={announcement.id}
                  className="bg-white rounded-lg border shadow-sm p-4 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-4">
                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <h3 className="font-semibold text-base">{announcement.title}</h3>
                        <Badge
                          className={
                            announcement.priority === "urgent"
                              ? "bg-red-100 text-red-700 border-red-200"
                              : "bg-blue-100 text-blue-700 border-blue-200"
                          }
                        >
                          {announcement.priority === "urgent" ? (
                            <AlertTriangle className="h-3 w-3 mr-1" />
                          ) : null}
                          {announcement.priority}
                        </Badge>
                        {announcement.isBlocking ? (
                          <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                            Acknowledgment Required
                          </Badge>
                        ) : (
                          <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                            FYI · views tracked
                          </Badge>
                        )}
                        {announcement.recipientUserIds && announcement.recipientUserIds.length > 0 && (
                          <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                            Sent to {announcement.recipientUserIds.length} {announcement.recipientUserIds.length === 1 ? "person" : "people"}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">
                        {truncateText(announcement.body, 100)}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          {announcement.isBlocking ? (
                            <Users className="h-3 w-3" />
                          ) : (
                            <Eye className="h-3 w-3" />
                          )}
                          {ackCount} {announcement.isBlocking ? "acknowledged" : "viewed"}
                        </span>
                        <span>{formatDate(announcement.createdAt)}</span>
                        {announcement.creator && (
                          <span>
                            by {announcement.creator.firstName} {announcement.creator.lastName}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => goToDetail(announcement.id)}
                        title="View details"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => goToEdit(announcement)}
                        title="Edit"
                      >
                        <Edit3 className="h-4 w-4" />
                      </Button>
                      {confirmDeleteId === announcement.id ? (
                        <>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleDelete(announcement.id)}
                            disabled={deleteMutation.isPending}
                          >
                            {deleteMutation.isPending ? "..." : "Confirm"}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setConfirmDeleteId(null)}
                          >
                            Cancel
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setConfirmDeleteId(announcement.id)}
                          title="Delete"
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <span className="text-sm text-muted-foreground">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ---------- RENDER: Create / Edit Form ----------
  if (view === "create" || view === "edit") {
    const isSubmitting = createMutation.isPending || updateMutation.isPending;
    const isFormValid = formData.title.trim().length > 0 && formData.body.trim().length > 0;

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goToList}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">
            {view === "create" ? "Create Announcement" : "Edit Announcement"}
          </h2>
        </div>

        <Card>
          <CardContent className="pt-6 space-y-5">
            {/* Title */}
            <div className="space-y-1.5">
              <label htmlFor="announcement-title" className="text-sm font-medium">
                Title
              </label>
              <Input
                id="announcement-title"
                placeholder="Enter announcement title..."
                value={formData.title}
                onChange={(e) => handleFormChange("title", e.target.value)}
              />
            </div>

            {/* Body */}
            <div className="space-y-1.5">
              <label htmlFor="announcement-body" className="text-sm font-medium">
                Message
              </label>
              <Textarea
                id="announcement-body"
                placeholder="Enter your announcement message..."
                rows={8}
                value={formData.body}
                onChange={(e) => handleFormChange("body", e.target.value)}
              />
            </div>

            {/* Priority */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Priority</label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={formData.priority === "normal" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFormChange("priority", "normal")}
                >
                  Normal
                </Button>
                <Button
                  type="button"
                  variant={formData.priority === "urgent" ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleFormChange("priority", "urgent")}
                  className={
                    formData.priority === "urgent"
                      ? "bg-red-600 hover:bg-red-700 text-white"
                      : ""
                  }
                >
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  Urgent
                </Button>
              </div>
            </div>

            {/* Acknowledgment toggle — controls whether agents must read and
                agree before they can dismiss the announcement (isBlocking).
                Optional acknowledgment lets agents close the popup without
                explicit agree (still tracked, just not enforced). */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Acknowledgment</label>
              <button
                type="button"
                onClick={() => handleFormChange("isBlocking", !formData.isBlocking)}
                aria-pressed={!!formData.isBlocking}
                className={`flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-colors ${
                  formData.isBlocking
                    ? "bg-accent/30 border-primary/30"
                    : "bg-background hover:bg-accent/20"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 ${
                    formData.isBlocking
                      ? "bg-primary border-primary"
                      : "border-input"
                  }`}
                >
                  {formData.isBlocking && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {formData.isBlocking ? "Acknowledgment Required" : "Acknowledgment Optional"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formData.isBlocking
                      ? "Agents must read and agree before continuing"
                      : "Agents can dismiss without acknowledging"}
                  </p>
                </div>
              </button>
            </div>

            {/* New agents — matches the Acknowledgment toggle above.
                Agents who join AFTER an announcement is posted no longer receive it, so
                a new hire's first login isn't the agency's whole back-catalogue stacked
                up as blocking modals. This opts a specific announcement back in for
                things that apply regardless of start date. */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">New agents</label>
              <button
                type="button"
                onClick={() => handleFormChange("notifyNewAgents", !formData.notifyNewAgents)}
                aria-pressed={!!formData.notifyNewAgents}
                className={`flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-colors ${
                  formData.notifyNewAgents
                    ? "bg-accent/30 border-primary/30"
                    : "bg-background hover:bg-accent/20"
                }`}
              >
                <div
                  className={`flex items-center justify-center w-5 h-5 rounded border-2 flex-shrink-0 ${
                    formData.notifyNewAgents ? "bg-primary border-primary" : "border-input"
                  }`}
                >
                  {formData.notifyNewAgents && (
                    <CheckCircle2 className="h-3.5 w-3.5 text-white" />
                  )}
                </div>
                <div>
                  <p className="text-sm font-medium">
                    {formData.notifyNewAgents
                      ? "Also show to agents who join later"
                      : "Only agents in the agency today"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formData.notifyNewAgents
                      ? "Anyone who joins from now on will see this too — for policies and requirements that always apply"
                      : "Agents who join after today start clean and won't be shown this"}
                  </p>
                </div>
              </button>
            </div>

            {/* Audience: everyone below you, a few named people, or every owner below you. */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Who gets this</label>
              <div className="grid gap-2 sm:grid-cols-3">
                {([
                  { key: "everyone", title: "Everyone", body: "Every agent and owner in your agency and all agencies below it." },
                  { key: "people", title: "Certain people", body: "Pick one or more agents or owners by name." },
                  { key: "owners", title: "All owners below you", body: "The owner of every agency under yours, and only them." },
                ] as const).map((option) => {
                  const selected = audienceMode === option.key;
                  return (
                    <button
                      key={option.key}
                      type="button"
                      onClick={() => void chooseAudience(option.key)}
                      aria-pressed={selected}
                      className={`flex items-start gap-3 rounded-lg border p-3 text-left transition-colors ${selected ? "bg-accent/30 border-primary/30" : "bg-background hover:bg-accent/20"}`}
                    >
                      <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border-2 ${selected ? "bg-primary border-primary" : "border-input"}`}>
                        {selected && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                      </span>
                      <span>
                        <span className="block text-sm font-medium">{option.title}</span>
                        <span className="block text-xs text-muted-foreground">{option.body}</span>
                      </span>
                    </button>
                  );
                })}
              </div>

              {audienceMode !== "everyone" && (
                <div className="rounded-lg border p-3 space-y-3">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium">
                      {recipients.length === 0
                        ? "No one picked yet"
                        : `${recipients.length} ${recipients.length === 1 ? "person" : "people"} will get this`}
                    </p>
                    {isAddingOwners && <span className="text-xs text-muted-foreground">Loading owners…</span>}
                  </div>
                  {recipients.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {recipients.map((r) => (
                        <span key={r.userId} className="inline-flex items-center gap-1 rounded-full border bg-accent/30 px-2.5 py-1 text-xs">
                          <span className="font-medium">{r.name}</span>
                          <span className="text-muted-foreground">· {r.agencyName}</span>
                          <button type="button" onClick={() => removeRecipient(r.userId)} aria-label={`Remove ${r.name}`} className="ml-0.5 rounded-full px-1 text-muted-foreground hover:text-foreground">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                  <Input
                    value={audienceQuery}
                    onChange={(e) => setAudienceQuery(e.target.value)}
                    placeholder={audienceMode === "owners" ? "Add someone else by name, email or agency" : "Search by name, email or agency"}
                  />
                  <ul className="max-h-56 overflow-y-auto divide-y rounded-md border">
                    {audienceResults.filter((m) => !recipients.some((r) => r.userId === m.userId)).map((m) => (
                      <li key={m.userId} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                        <span className="min-w-0">
                          <span className="block truncate font-medium">{m.name}</span>
                          <span className="block truncate text-xs text-muted-foreground">{m.agencyName} · {m.role === "owner" || m.role === "agency_owner" ? "Owner" : "Agent"} · {m.email}</span>
                        </span>
                        <Button type="button" size="sm" variant="outline" onClick={() => addRecipient(m)}>Add</Button>
                      </li>
                    ))}
                    {!isSearchingAudience && audienceResults.filter((m) => !recipients.some((r) => r.userId === m.userId)).length === 0 && (
                      <li className="px-3 py-3 text-xs text-muted-foreground">{audienceQuery ? "No matches under you." : "Showing the first 25 people under you. Type to narrow it down."}</li>
                    )}
                  </ul>
                </div>
              )}
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={goToList} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!isFormValid || isSubmitting || (targeted && recipients.length === 0)}
              >
                {isSubmitting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : null}
                {view === "create" ? "Create Announcement" : "Update Announcement"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---------- RENDER: Detail View ----------
  if (view === "detail") {
    const detail = detailData;
    const announcement = detail?.announcement;

    if (isLoadingDetail || !announcement) {
      return (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={goToList}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-xl font-semibold">Announcement Details</h2>
          </div>
          <div className="flex items-center justify-center p-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading details...</p>
            </div>
          </div>
        </div>
      );
    }

    const acknowledged = detail?.acknowledged ?? [];
    const pending = detail?.pending ?? [];

    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={goToList}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-xl font-semibold">Announcement Details</h2>
        </div>

        {/* Announcement content */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="text-lg">{announcement.title}</CardTitle>
                <div className="ann-meta-row flex items-center gap-2 mt-2">
                  <Badge
                    className={
                      announcement.priority === "urgent"
                        ? "bg-red-100 text-red-700 border-red-200"
                        : "bg-blue-100 text-blue-700 border-blue-200"
                    }
                  >
                    {announcement.priority === "urgent" ? (
                      <AlertTriangle className="h-3 w-3 mr-1" />
                    ) : null}
                    {announcement.priority}
                  </Badge>
                  {announcement.isBlocking ? (
                    <Badge className="bg-orange-100 text-orange-700 border-orange-200">
                      Acknowledgment Required
                    </Badge>
                  ) : (
                    <Badge className="bg-gray-100 text-gray-600 border-gray-200">
                      FYI · views tracked
                    </Badge>
                  )}
                  {announcement.recipientUserIds && announcement.recipientUserIds.length > 0 && (
                    <Badge className="bg-violet-100 text-violet-700 border-violet-200">
                      Sent to {announcement.recipientUserIds.length} {announcement.recipientUserIds.length === 1 ? "person" : "people"}
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground">
                    Created {formatDateTime(announcement.createdAt)}
                  </span>
                </div>
              </div>
              <div className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => goToEdit(announcement)}
                  title="Edit"
                >
                  <Edit3 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{announcement.body}</p>
          </CardContent>
        </Card>

        {/* Acknowledgment / view status. For FYI (non-required) announcements
            the same records are VIEWS — agents pressed "Got it" at sign-in —
            so the boxes read "Viewed" / "Not Viewed Yet" instead. */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Acknowledged / Viewed */}
          <Card className="border-green-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                {announcement.isBlocking ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <Eye className="h-5 w-5 text-green-600" />
                )}
                <CardTitle className="text-base">
                  {announcement.isBlocking ? "Acknowledged" : "Viewed"} ({acknowledged.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {acknowledged.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {announcement.isBlocking
                    ? "No acknowledgments yet."
                    : "No views yet — agents see this at sign-in and press “Got it”."}
                </p>
              ) : (
                <ul className="space-y-2 max-h-[200px] overflow-y-auto">
                  {acknowledged.map((person) => (
                    <li
                      key={person.userId}
                      className="flex items-start justify-between gap-3 text-sm"
                    >
                      {/* Full name always visible — wraps instead of truncating.
                          Agency label sits under it so it can't squeeze the name. */}
                      <div className="flex items-start gap-2 min-w-0">
                        <CheckCircle2 className="h-3.5 w-3.5 text-green-500 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <span className="break-words">{person.name}</span>
                          {person.agencyName && (
                            <span className="block text-xs text-muted-foreground">{person.agencyName}</span>
                          )}
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground flex-shrink-0 whitespace-nowrap">
                        {formatDateTime(person.acknowledgedAt)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          {/* Pending / Not Viewed Yet */}
          <Card className="border-amber-200">
            <CardHeader className="pb-3">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-amber-600" />
                <CardTitle className="text-base">
                  {announcement.isBlocking ? "Pending" : "Not Viewed Yet"} ({pending.length})
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {pending.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  {announcement.isBlocking ? "Everyone has acknowledged." : "Everyone has viewed this."}
                </p>
              ) : (
                <ul className="space-y-2 max-h-[200px] overflow-y-auto">
                  {pending.map((person) => (
                    <li
                      key={person.userId}
                      className="flex items-start gap-2 text-sm"
                    >
                      <Clock className="h-3.5 w-3.5 text-amber-500 flex-shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <span className="break-words">{person.name}</span>
                        {person.agencyName && (
                          <span className="block text-xs text-muted-foreground">{person.agencyName}</span>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Delete button */}
        <div className="flex justify-end gap-2">
          {confirmDeleteId === announcement.id ? (
            <>
              <Button
                variant="ghost"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={() => handleDelete(announcement.id)}
                disabled={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                ) : (
                  <Trash2 className="h-4 w-4 mr-2" />
                )}
                Confirm Delete
              </Button>
            </>
          ) : (
            <Button
              variant="destructive"
              onClick={() => setConfirmDeleteId(announcement.id)}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete Announcement
            </Button>
          )}
        </div>
      </div>
    );
  }

  // Fallback (should never reach here)
  return null;
}
